import React, { useEffect, useState, useRef } from 'react';
import { API_BASE } from '../../../services/apiClient';
import { CONTACT_DEPARTMENTS, DEFAULT_DEPARTMENT, getDepartmentLabel } from '../../../config/contactDepartments';

const getUserInfo = () => {
  const token = localStorage.getItem('auth_token');
  let nome = '';
  let sobrenome = '';
  let codigo = '';
  let role = '';

  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      nome = payload.nome || '';
      sobrenome = payload.sobrenome || '';
      codigo = payload.codigo || '';
      role = (payload.role || payload.perfil || '').toString().toUpperCase();
    } catch (e) {
      /* ignore */
    }
  }

  const fullName = [nome, sobrenome].filter(Boolean).join(' ').trim();
  return { nome, fullName, codigo, role };
};

const guessContentTypeFromName = (name = '') => {
  if (!name || typeof name !== 'string') return '';
  const lowered = name.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(lowered)) return 'image/*';
  if (/\.(pdf)$/i.test(lowered)) return 'application/pdf';
  if (/\.(txt|log)$/i.test(lowered)) return 'text/plain';
  return '';
};

const STATUS_LABELS = {
  aberto: { label: 'Em análise', color: '#f5a623' },
  em_andamento: { label: 'Em andamento', color: '#f5a623' },
  aguardando: { label: 'Em análise', color: '#f5a623' },
  pending: { label: 'Em análise', color: '#f5a623' },
  resolved: { label: 'Resolvida', color: '#22c55e' },
  resolvido: { label: 'Resolvida', color: '#22c55e' },
  rejected: { label: 'Rejeitada', color: '#d32f2f' },
  fechado: { label: 'Fechado', color: '#d32f2f' },
};

const API_URL = import.meta.env.VITE_URL_API;

const buildSupportUrl = (path) => {
  const base = (API_URL || API_BASE || '').replace(/\/$/, '');
  if (!base) return path; // fallback: let fetch handle relative (dev proxy)
  const normalized = /\/api$/i.test(base) && /^\/api\b/.test(path) ? `${base}${path.replace(/^\/api/i, '')}` : `${base}${path}`;
  return normalized.replace(/([^:]\/)\/+/, '$1/');
};

const buildAttachmentUrl = (file) => {
  const dataField = (() => {
    if (!file?.data) return '';
    if (typeof file.data === 'string') {
      try {
        const parsed = JSON.parse(file.data);
        return parsed?.url || parsed?.link || parsed?.path || parsed?.file || parsed?.filename || file.data;
      } catch (_) {
        return file.data;
      }
    }
    if (typeof file.data === 'object') {
      return file.data.url || file.data.link || file.data.path || file.data.file || file.data.filename || '';
    }
    return '';
  })();

  const raw =
    (typeof file === 'string' && file) ||
    file?.url ||
    file?.link ||
    file?.path ||
    file?.file ||
    file?.fileUrl ||
    file?.file_url ||
    dataField ||
    '';
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  // Evita prefixar /api em caminhos de storage ou públicos
  const needsLeadingSlash = !raw.startsWith('/');
  if (/^(storage|public)\//i.test(raw)) {
    return buildSupportUrl(`${needsLeadingSlash ? '/' : ''}${raw}`);
  }
  return buildSupportUrl(raw.startsWith('/') ? raw : `/api${raw.startsWith('api') ? '' : raw.startsWith('/') ? '' : '/'}${raw}`);
};

const extractFileNameFromUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  try {
    const withoutQuery = raw.split('?')[0].split('#')[0];
    const parts = withoutQuery.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    return decodeURIComponent(last || '').trim();
  } catch (_) {
    return '';
  }
};

const getAttachmentName = (file, idx = 0) => {
  if (typeof file === 'string') {
    const fromUrl = extractFileNameFromUrl(file);
    return fromUrl || `Anexo ${idx + 1}`;
  }

  const nameField =
    file?.filename ||
    file?.name ||
    file?.originalName ||
    file?.originalname ||
    file?.fileName ||
    file?.file ||
    (typeof file?.data === 'object' ? file.data?.filename || file.data?.name : undefined);

  const urlField = file?.url || file?.link || file?.path || file?.fileUrl || file?.file_url || (typeof file?.data === 'string' ? file.data : file?.data?.path);
  const fallbackFromUrl = extractFileNameFromUrl(urlField);

  return nameField || fallbackFromUrl || `Anexo ${idx + 1}`;
};

// A API envia senderType como 'suporte' (equipe) ou 'usuario' (cliente)
const isMessageFromSupport = (msg) => {
  const senderType = String(msg?.senderType || '').toLowerCase();
  return senderType === 'suporte' || senderType === 'support' || senderType === 'admin';
};

const isMessageFromOtherSide = (msg, role) =>
  role === 'ADMIN' ? !isMessageFromSupport(msg) : isMessageFromSupport(msg);

const getMessagePresentation = (msg, role, currentUserName) => {
  const senderType = String(msg?.senderType || '').toLowerCase();
  const hasKnownSenderType = ['suporte', 'support', 'admin', 'usuario', 'cliente', 'user'].includes(senderType);
  const isOwnMessage = hasKnownSenderType
    ? (role === 'ADMIN' ? isMessageFromSupport(msg) : !isMessageFromSupport(msg))
    : Boolean(msg?.isFromUser);
  const isSupportMessage = hasKnownSenderType ? isMessageFromSupport(msg) : !isOwnMessage;
  const senderName = isOwnMessage
    ? (currentUserName || 'Você')
    : (isSupportMessage ? 'Equipe JoviPlanet' : (msg?.senderName || 'Cliente'));

  return {
    isOwnMessage,
    isSupportMessage,
    senderName,
    senderLabel: isOwnMessage ? 'Você' : (isSupportMessage ? 'ADMIN / Suporte' : 'Cliente'),
    initials: senderName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('') || '?',
  };
};

// Prioriza a contagem calculada pela API (dataLeituraMSGSUPORTE); só usa fallback local se ausente
const getTicketUnreadCount = (ticket, role) => {
  const fromApi = Number(ticket?.unreadCount);
  if (Number.isFinite(fromApi)) return fromApi;
  if (!Array.isArray(ticket?.messages)) return 0;
  return ticket.messages.filter((msg) => isMessageFromOtherSide(msg, role) && !msg.readAt).length;
};

const sumUnreadCount = (list, role) =>
  (Array.isArray(list) ? list : []).reduce((count, ticket) => count + getTicketUnreadCount(ticket, role), 0);

const sortTickets = (list, role) => [...(Array.isArray(list) ? list : [])].sort((a, b) => {
  const unreadDifference = getTicketUnreadCount(b, role) - getTicketUnreadCount(a, role);
  if (unreadDifference !== 0) return unreadDifference;

  const dateA = new Date(a.updatedAt || a.lastMessageAt || a.createdAt || a.date || 0).getTime();
  const dateB = new Date(b.updatedAt || b.lastMessageAt || b.createdAt || b.date || 0).getTime();
  if (dateB !== dateA) return dateB - dateA;
  return Number(b.id || 0) - Number(a.id || 0);
});

const formatLastInteraction = (ticket, role, currentUserName) => {
  const messages = ticket?.messages || [];
  const lastMsg = messages[messages.length - 1];
  if (!lastMsg) {
    return { text: '—', label: '', time: '' };
  }

  const presentation = getMessagePresentation(lastMsg, role, currentUserName);
  const date = new Date(lastMsg.createdAt || ticket.lastMessageAt);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(startToday);
  yesterday.setDate(yesterday.getDate() - 1);

  let dateText;
  if (date >= startToday) dateText = 'Hoje';
  else if (date >= yesterday) dateText = 'Ontem';
  else dateText = date.toLocaleDateString('pt-BR');

  const timeText = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const raw = (lastMsg.content || '').replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').trim();
  const text = raw.length > 40 ? `${raw.slice(0, 40)}…` : raw;

  return { text, label: presentation.senderLabel, time: `${dateText} ${timeText}` };
};

const getTicketOrigin = (ticket, currentFullName) => {
  const createdBy = (ticket?.createdByName || '').trim().toLowerCase();
  const user = (currentFullName || '').trim().toLowerCase();
  if (!createdBy) return 'Recebido';
  if (user && (createdBy === user || createdBy.startsWith(user))) return 'Enviado';
  return 'Recebido';
};

const ARCHIVED_STATUSES = ['resolvido', 'fechado', 'resolved', 'rejected'];

const isTicketArchived = (ticket, archiveDays = 30) => {
  if (ARCHIVED_STATUSES.includes(ticket?.status)) return true;
  const updated = new Date(ticket?.updatedAt || ticket?.lastMessageAt || ticket?.createdAt || ticket?.date || 0).getTime();
  const cutoff = Date.now() - archiveDays * 24 * 60 * 60 * 1000;
  return updated < cutoff;
};

const STATUS_FILTER_MAP = {
  em_analise: ['aberto', 'em_andamento', 'aguardando', 'pending'],
  aberto: ['aberto'],
  em_andamento: ['em_andamento'],
  aguardando: ['aguardando'],
  resolvido: ['resolvido', 'resolved'],
  fechado: ['fechado', 'rejected'],
};

const filterTickets = (list, filters) => {
  let result = Array.isArray(list) ? [...list] : [];

  if (filters.status && STATUS_FILTER_MAP[filters.status]) {
    const allowed = STATUS_FILTER_MAP[filters.status];
    result = result.filter((t) => allowed.includes(t.status));
  } else if (filters.status) {
    result = result.filter((t) => t.status === filters.status);
  }

  result = result.filter((t) =>
    filters.archived ? isTicketArchived(t, filters.days) : !isTicketArchived(t, filters.days)
  );

  if (filters.customerUserId && String(filters.customerUserId).trim()) {
    const code = String(filters.customerUserId).trim();
    result = result.filter(
      (t) => String(t.customerUserId) === code || String(t.hostUserId) === code
    );
  }

  if (filters.departamento) {
    result = result.filter((t) => String(t.departamento || '') === filters.departamento);
  }

  if (filters.search && String(filters.search).trim()) {
    const term = String(filters.search).trim().toLowerCase();
    result = result.filter((t) => {
      const fields = [
        String(t.id || ''),
        String(t.subject || t.description || t.descricao || ''),
        String(t.createdByName || ''),
        String(t.relatedHostName || ''),
        String(t.customerUserId || ''),
        String(t.hostUserId || ''),
        String(t.departamento || ''),
      ];
      return fields.some((f) => f.toLowerCase().includes(term));
    });
  }

  return result;
};

const ContactMediation = () => {
  const { nome, fullName, codigo, role } = getUserInfo();

  const [message, setMessage] = useState('');
  const [department, setDepartment] = useState(DEFAULT_DEPARTMENT);
  const [mediations, setMediations] = useState([]);
  const [mediationsLoading, setMediationsLoading] = useState(true);
  const [mediationsError, setMediationsError] = useState(null);
  const [selectedMediation, setSelectedMediation] = useState(null);
  const [fileToAttach, setFileToAttach] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [adminStatus, setAdminStatus] = useState('aberto');
  const [savingPatch, setSavingPatch] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [previewType, setPreviewType] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [customerUserId, setCustomerUserId] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(13);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [replySuccess, setReplySuccess] = useState(false);
  const [showNewMessageAlert, setShowNewMessageAlert] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterArchived, setFilterArchived] = useState(false);
  const [filterDays, setFilterDays] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const lastKnownUnreadCountRef = useRef(0);
  const autoOpenedTicketRef = useRef(false);

  const viewAttachment = async (file, idx = 0) => {
    const url = buildAttachmentUrl(file);
    if (!url) return;
    const rawCandidate =
      (typeof file === 'string' && file) ||
      file?.url ||
      file?.link ||
      file?.path ||
      file?.file ||
      file?.fileUrl ||
      file?.file_url ||
      '';
    const fallbackCandidates = [];
    if (url.includes('/api/')) fallbackCandidates.push(url.replace('/api/', '/'));
    if (rawCandidate && !rawCandidate.includes('/')) {
      fallbackCandidates.push(buildSupportUrl(`/storage/${rawCandidate}`));
      fallbackCandidates.push(buildSupportUrl(`/public/${rawCandidate}`));
      fallbackCandidates.push(buildSupportUrl(`/${rawCandidate}`));
    }

    const tryFetch = async (targetUrl) => {
      const needsAuth = targetUrl.startsWith('/') || (API_URL && targetUrl.includes(API_URL));
      if (!needsAuth) {
        setPreviewUrl(targetUrl);
        setPreviewName(getAttachmentName(file, idx));
        setPreviewType(guessContentTypeFromName(getAttachmentName(file, idx)) || guessContentTypeFromName(targetUrl));
        return true;
      }
      const resp = await fetch(targetUrl, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (!resp.ok) return false;
      const blob = await resp.blob();
      const contentType = resp.headers.get('content-type') || blob.type || '';
      const objectUrl = window.URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      setPreviewName(getAttachmentName(file, idx));
      setPreviewType(contentType || guessContentTypeFromName(getAttachmentName(file, idx)));
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 30000);
      return true;
    };

    try {
      const ok = await tryFetch(url);
      if (ok) return;
      for (const alt of fallbackCandidates) {
        const altOk = await tryFetch(alt);
        if (altOk) return;
      }
      alert('Não foi possível abrir o anexo. Verifique se o arquivo existe ou se a URL está acessível.');
    } catch (err) {
      console.error('[view] erro ao abrir anexo', err);
      alert('Não foi possível abrir o anexo. Tente novamente.');
    }
  };

  useEffect(() => {
    const fetchMediations = async () => {
      setMediationsLoading(true);
      setMediationsError(null);
      try {
        const url = buildSupportUrl('/api/support/tickets?limit=1000');

        const resp = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            Accept: 'application/json',
          },
        });

        const contentType = resp.headers.get('content-type') || '';
        if (!resp.ok) {
          const bodyText = await resp.text();
          throw new Error(`Erro ao listar mediações (${resp.status}): ${bodyText}`);
        }
        if (!contentType.includes('application/json')) {
          const bodyText = await resp.text();
          throw new Error(`Resposta não-JSON da API em ${resp.url || url}: ${bodyText}`);
        }
        const data = await resp.json();
        const list = Array.isArray(data) ? data : data?.data || [];
        const sortedList = sortTickets(list, role);

        const filters = {
          status: filterStatus,
          archived: filterArchived,
          days: filterDays,
          search,
          customerUserId: filterCustomer,
          departamento: filterDepartment,
        };
        const filteredList = filterTickets(sortedList, filters);

        // Mensagens não lidas: usa a contagem persistida pela API
        const unread = sumUnreadCount(filteredList, role);

        console.log('[fetch] cálculo de unread', { role, totalTickets: list.length, filtered: filteredList.length, unread });

        // Verificar se houve aumento de mensagens não lidas do outro lado
        if (unread > lastKnownUnreadCountRef.current && lastKnownUnreadCountRef.current > 0) {
          console.log('[fetch] nova mensagem detectada (unread)', { previous: lastKnownUnreadCountRef.current, current: unread });
          setShowNewMessageAlert(true);
          setTimeout(() => setShowNewMessageAlert(false), 5000);
        }

        setUnreadCount(unread);
        lastKnownUnreadCountRef.current = unread;

        // Paginação no lado do cliente
        const startIndex = (currentPage - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedList = filteredList.slice(startIndex, endIndex);
        setMediations(paginatedList);
        setTotalPages(Math.ceil(filteredList.length / limit) || 1);

        // Abertura automática do ticket vindo do link do e-mail (?ticket=123)
        if (!autoOpenedTicketRef.current) {
          const ticketParam = new URLSearchParams(window.location.search).get('ticket');
          if (ticketParam) {
            const target = sortedList.find((t) => String(t.id) === String(ticketParam));
            if (target) {
              autoOpenedTicketRef.current = true;
              setSelectedMediation(target);
            }
          }
        }
      } catch (err) {
        console.error(err);
        setMediationsError('Não foi possível carregar suas mediações.');
      } finally {
        setMediationsLoading(false);
      }
    };

    fetchMediations();
  }, [currentPage, limit, role, filterStatus, filterArchived, filterDays, search, filterCustomer, filterDepartment]);

  // Debounce do campo de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reseta a página quando filtros (exceto busca) mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterArchived, filterDays, filterCustomer, filterDepartment]);

  // Polling para verificar novas mensagens a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const url = buildSupportUrl('/api/support/tickets?limit=1000');
        const resp = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            Accept: 'application/json',
          },
        });

        if (resp.ok) {
          const data = await resp.json();
          const list = Array.isArray(data) ? data : data?.data || [];
          const sortedList = sortTickets(list, role);

          const filters = {
            status: filterStatus,
            archived: filterArchived,
            days: filterDays,
            search,
            customerUserId: filterCustomer,
            departamento: filterDepartment,
          };
          const filteredList = filterTickets(sortedList, filters);

          // Mensagens não lidas: usa a contagem persistida pela API
          const unread = sumUnreadCount(filteredList, role);

          console.log('[polling] verificação', { totalTickets: list.length, filtered: filteredList.length, unread, lastKnownUnread: lastKnownUnreadCountRef.current });

          // Verificar se houve aumento de mensagens não lidas do outro lado
          if (unread > lastKnownUnreadCountRef.current && lastKnownUnreadCountRef.current > 0) {
            console.log('[polling] nova mensagem detectada (unread)', { previous: lastKnownUnreadCountRef.current, current: unread });
            setShowNewMessageAlert(true);
            setTimeout(() => setShowNewMessageAlert(false), 5000);
          }

          setUnreadCount(unread);
          lastKnownUnreadCountRef.current = unread;

          // Atualizar lista se houver mudança
          const startIndex = (currentPage - 1) * limit;
          const endIndex = startIndex + limit;
          const paginatedList = filteredList.slice(startIndex, endIndex);
          setMediations(paginatedList);
          setTotalPages(Math.ceil(filteredList.length / limit) || 1);
        }
      } catch (err) {
        console.error('[polling] erro ao verificar novas mensagens', err);
      }
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [currentPage, limit, role, filterStatus, filterArchived, filterDays, search, filterCustomer, filterDepartment]);

  useEffect(() => {
    if (selectedMediation) {
      setPreviewUrl('');
      setPreviewName('');
      setPreviewType('');
      setAdminStatus(selectedMediation.status || 'aberto');
      console.log('[ui] selectedMediation mudou', {
        id: selectedMediation.id,
        attachments: selectedMediation.attachments,
      });

      // Marcar mensagens do outro lado como lidas (backend + estado local)
      const ticketUnread = getTicketUnreadCount(selectedMediation, role);
      if (ticketUnread > 0) {
        const markMessagesAsRead = async () => {
          try {
            const url = buildSupportUrl(`/api/support/tickets/${selectedMediation.id}/mark-read`);
            const resp = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
              },
            });
            if (!resp.ok) {
              const bodyText = await resp.text();
              throw new Error(`Erro ao marcar como lida (${resp.status}): ${bodyText}`);
            }
            console.log('[markAsRead] mensagens marcadas como lidas', { ticketId: selectedMediation.id, count: ticketUnread });
          } catch (err) {
            console.error('[markAsRead] erro ao marcar mensagens como lidas', err);
          }
        };

        markMessagesAsRead();

        const readNow = new Date().toISOString();
        const updatedMessages = (selectedMediation.messages || []).map(msg =>
          isMessageFromOtherSide(msg, role) && !msg.readAt ? { ...msg, readAt: readNow } : msg
        );
        setSelectedMediation(prev => ({ ...prev, unreadCount: 0, messages: updatedMessages }));
        setMediations(prev => prev.map(m =>
          m.id === selectedMediation.id ? { ...m, unreadCount: 0, messages: updatedMessages } : m
        ));
        setUnreadCount(prev => {
          const next = Math.max(0, prev - ticketUnread);
          lastKnownUnreadCountRef.current = next;
          return next;
        });
      }
      const hasAttachments = Array.isArray(selectedMediation.attachments) && selectedMediation.attachments.length > 0;
      if (!hasAttachments) {
        const load = async () => {
          try {
            const url = buildSupportUrl(`/api/support/tickets/${selectedMediation.id}/attachments`);
            const resp = await fetch(url, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
                Accept: 'application/json',
              },
            });
            const contentType = resp.headers.get('content-type') || '';
            const text = await resp.text();
            let data = [];
            try { data = text ? JSON.parse(text) : []; } catch (_) { data = []; }
            if (!resp.ok) {
              console.error('[attachments] erro ao buscar', { status: resp.status, statusText: resp.statusText, contentType, text });
              return;
            }
            const list = Array.isArray(data) ? data : data?.data || [];
            console.log('[attachments] carregados do servidor', { ticketId: selectedMediation.id, total: list.length, list });
            if (list.length) {
              setMediations((prev) => prev.map((m) => (m.id === selectedMediation.id ? { ...m, attachments: list } : m)));
              setSelectedMediation((prev) => ({ ...prev, attachments: list }));
            }
          } catch (err) {
            console.error('[attachments] exceção ao buscar', err);
          }
        };
        load();
      }
    }
  }, [selectedMediation, role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[handleSubmit] Iniciando envio do ticket');
    if (!message.trim()) {
      console.log('[handleSubmit] Mensagem vazia, abortando');
      return;
    }
    if (isSubmitting) {
      console.log('[handleSubmit] Já está enviando, abortando');
      return;
    }
    setIsSubmitting(true);
    try {
      const ticketUrl = buildSupportUrl('/api/support/tickets');
      const payload = { descricao: message.trim(), departamento: department };
      
      if (role === 'ADMIN' && customerUserId.trim()) {
        payload.customerUserId = customerUserId.trim();
      }
      
      console.log('[handleSubmit] Enviando requisição para', ticketUrl, payload);
      const resp = await fetch(ticketUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify(payload),
      });
      
      console.log('[handleSubmit] Resposta da API:', resp.status);
      
      if (!resp.ok) {
        let errorMsg = 'Não foi possível enviar sua solicitação. Tente novamente.';
        
        try {
          const errorJson = await resp.json();
          console.log('[handleSubmit] Erro JSON:', errorJson);
          if (errorJson.error) {
            errorMsg = errorJson.error;
            // Traduzir mensagens comuns em inglês
            if (errorMsg.includes('Client not found') || errorMsg.includes('Cliente não encontrado')) {
              errorMsg = 'Cliente não encontrado. Por favor, verifique o ID do cliente e tente novamente.';
            } else if (errorMsg.includes('Cannot create ticket for another administrator') || errorMsg.includes('Não é possível criar ticket para outro administrador')) {
              errorMsg = 'Não é possível criar ticket para outro administrador.';
            }
          }
        } catch (e) {
          console.error('[handleSubmit] Erro ao parsear JSON de erro:', e);
          errorMsg = `Erro ${resp.status}: Não foi possível enviar sua solicitação. Tente novamente.`;
        }
        
        console.error('[handleSubmit] Erro na resposta:', errorMsg);
        setErrorMessage(errorMsg);
        setShowErrorModal(true);
        setIsSubmitting(false);
        throw new Error(`Erro ao criar mediação (${resp.status})`);
      }
      
      const responseData = await resp.json();
      console.log('[handleSubmit] Ticket criado com sucesso:', responseData);
      console.log('[handleSubmit] Limpando formulário e mostrando modal');
      setMessage('');
      setCustomerUserId('');
      setDepartment(DEFAULT_DEPARTMENT);
      setCurrentPage(1);
      setSubmitSuccess(true);
      setShowSuccessModal(true);
      console.log('[handleSubmit] Estados definidos: submitSuccess=true, showSuccessModal=true');
      // Recarregar a lista completa para incluir o novo ticket
      const reloadUrl = buildSupportUrl('/api/support/tickets?limit=1000');
      const reloadResp = await fetch(reloadUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          Accept: 'application/json',
        },
      });
      if (reloadResp.ok) {
        const reloadData = await reloadResp.json();
        const reloadList = Array.isArray(reloadData) ? reloadData : reloadData?.data || [];
        const sortedReloadList = sortTickets(reloadList, role);

        const filters = {
          status: filterStatus,
          archived: filterArchived,
          days: filterDays,
          search,
          customerUserId: filterCustomer,
          departamento: filterDepartment,
        };
        const filteredReloadList = filterTickets(sortedReloadList, filters);

        // Recalcular mensagens não lidas
        const unread = sumUnreadCount(filteredReloadList, role);
        setUnreadCount(unread);
        lastKnownUnreadCountRef.current = unread;

        const paginatedList = filteredReloadList.slice(0, limit);
        setMediations(paginatedList);
        setTotalPages(Math.ceil(filteredReloadList.length / limit) || 1);
        console.log('[handleSubmit] Lista de tickets atualizada');
      }
    } catch (err) {
      console.error('[handleSubmit] Erro no catch:', err);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setSubmitSuccess(false);
    setShowSuccessModal(false);
    setMessage('');
    setCustomerUserId('');
    setDepartment(DEFAULT_DEPARTMENT);
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedMediation) return;
    setSendingReply(true);
    try {
      console.log('[reply] enviando mensagem para ticket', { id: selectedMediation.id, url: `/api/support/tickets/${selectedMediation.id}/messages` });
      const url = buildSupportUrl(`/api/support/tickets/${selectedMediation.id}/messages`);
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ content: replyMessage.trim() }),
      });
      if (!resp.ok) {
        const bodyText = await resp.text();
        throw new Error(`Erro ao enviar resposta (${resp.status}): ${bodyText}`);
      }
      const newMsg = await resp.json();
      const formatted = {
        id: newMsg.id || newMsg.codigoMSGSUPORTE?.toString(),
        senderType: newMsg.senderType || (role === 'ADMIN' ? 'suporte' : 'usuario'),
        senderName: newMsg.senderName || nome,
        content: newMsg.content || newMsg.conteudoMSGSUPORTE || replyMessage.trim(),
        attachments: newMsg.attachments || [],
        createdAt: newMsg.createdAt || newMsg.dataEnvioMSGSUPORTE || new Date().toISOString(),
        readAt: null,
        isFromUser: true,
      };
      const updatedMessages = [...(selectedMediation.messages || []), formatted];
      setSelectedMediation((prev) => ({ ...prev, messages: updatedMessages }));
      setMediations((prev) => prev.map((m) => m.id === selectedMediation.id ? { ...m, messages: updatedMessages } : m));
      setReplyMessage('');
      setReplySuccess(true);
      setTimeout(() => setReplySuccess(false), 3000);
    } catch (err) {
      console.error(err);
      alert('Não foi possível enviar a resposta. Tente novamente.');
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedMediation) return;
    setSavingPatch(true);
    try {
      const url = buildSupportUrl(`/api/support/tickets/${selectedMediation.id}`);

      const resp = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          status: adminStatus,
        }),
      });

      const contentType = resp.headers.get('content-type') || '';
      if (!resp.ok) {
        const bodyText = await resp.text();
        throw new Error(`Erro ao atualizar mediação (${resp.status}): ${bodyText}`);
      }
      if (!contentType.includes('application/json')) {
        const bodyText = await resp.text();
        throw new Error(`Resposta não-JSON da API em ${resp.url || url}: ${bodyText}`);
      }
      const updated = await resp.json();
      const normalized = {
        ...selectedMediation,
        ...updated,
        status: updated.status || adminStatus,
        statusLabel: updated.statusLabel || STATUS_LABELS[updated.status || adminStatus]?.label,
        data: updated.data || updated.date || updated.createdAt,
      };
      setMediations((prev) => prev.map((m) => (m.id === normalized.id ? normalized : m)));
      setSelectedMediation(normalized);
      
      // Atualizar o status local para refletir a mudança imediatamente
      console.log('[handleUpdateStatus] Status atualizado com sucesso', { normalized });
    } catch (err) {
      console.error(err);
      alert('Não foi possível salvar as alterações.');
    } finally {
      setSavingPatch(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!fileToAttach || !selectedMediation) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', fileToAttach);
      const url = buildSupportUrl(`/api/support/tickets/${selectedMediation.id}/attachments`);

      console.log('[upload] iniciando', {
        url,
        ticketId: selectedMediation.id,
        fileName: fileToAttach?.name,
        fileType: fileToAttach?.type,
        fileSize: fileToAttach?.size,
      });

      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: fd,
      });

      if (!resp.ok) {
        const bodyText = await resp.text();
        console.error('[upload] erro', {
          status: resp.status,
          statusText: resp.statusText,
          contentType: resp.headers.get('content-type'),
          bodyText,
        });
        throw new Error(`Erro ao anexar arquivo (${resp.status}): ${bodyText || 'sem detalhes'}`);
      }
      const contentType = resp.headers.get('content-type') || '';
      const rawBody = await resp.text();
      let created = null;
      try {
        created = rawBody ? JSON.parse(rawBody) : null;
      } catch (_) {
        created = { raw: rawBody };
      }
      console.log('[upload] sucesso', { contentType, created });

      const normalizedCreated = {
        ...created,
        // Garante nome legível com fallback para o nome enviado
        name: created?.filename || created?.name || created?.originalName || created?.originalname || fileToAttach?.name,
        originalName: created?.originalName || created?.originalname || fileToAttach?.name,
      };

      const hasMetadata = normalizedCreated && (normalizedCreated.filename || normalizedCreated.name || normalizedCreated.url || normalizedCreated.path || normalizedCreated.link);
      if (!hasMetadata) {
        alert('Upload respondeu sem metadados do anexo. Veja console para detalhes.');
      }

      // Criar mensagem automática com o anexo para mostrar contexto claro de quem enviou e quando
      const messageUrl = buildSupportUrl(`/api/support/tickets/${selectedMediation.id}/messages`);
      const messageResp = await fetch(messageUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ 
          content: 'Arquivo anexado',
          attachments: [normalizedCreated]
        }),
      });

      if (messageResp.ok) {
        const newMsg = await messageResp.json();
        const formatted = {
          id: newMsg.id || newMsg.codigoMSGSUPORTE?.toString(),
          senderType: newMsg.senderType || (role === 'ADMIN' ? 'suporte' : 'usuario'),
          senderName: newMsg.senderName || nome,
          content: newMsg.content || newMsg.conteudoMSGSUPORTE || 'Arquivo anexado',
          attachments: newMsg.attachments || [normalizedCreated],
          createdAt: newMsg.createdAt || newMsg.dataEnvioMSGSUPORTE || new Date().toISOString(),
          readAt: null,
          isFromUser: true,
        };
        const updatedMessages = [...(selectedMediation.messages || []), formatted];
        setSelectedMediation((prev) => ({ ...prev, messages: updatedMessages }));
        setMediations((prev) => prev.map((m) => m.id === selectedMediation.id ? { ...m, messages: updatedMessages } : m));
      } else {
        // Fallback: se não conseguir criar mensagem, adiciona à lista de anexos
        setMediations((prev) =>
          prev.map((m) => (m.id === selectedMediation.id ? { ...m, attachments: [...(m.attachments || []), normalizedCreated] } : m))
        );
        setSelectedMediation((prev) => ({ ...prev, attachments: [...(prev.attachments || []), normalizedCreated] }));
      }

      console.log('[upload] attachments atualizados', {
        ticketId: selectedMediation.id,
        total: (selectedMediation.attachments || []).length + 1,
        novo: normalizedCreated,
      });
      setFileToAttach(null);
      e.target.reset();
    } catch (err) {
      console.error(err);
      alert(`Não foi possível anexar o arquivo. ${err.message || ''}`);
    } finally {
      setUploading(false);
    }
  };

  const renderStatus = (m) => (
    <span
      style={{
        display: 'inline-block',
        background: STATUS_LABELS[m.status]?.color || '#888',
        color: '#fff',
        borderRadius: 12,
        padding: '4px 12px',
        fontWeight: 600,
        fontSize: 13,
        textAlign: 'center',
        minWidth: '80px',
        lineHeight: '1.4',
        whiteSpace: 'nowrap',
      }}
    >
      {m.statusLabel || STATUS_LABELS[m.status]?.label || m.status || 'Desconhecido'}
    </span>
  );

  return (
    <div style={{ width: '100%', padding: '24px 0', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <h2 style={{ margin: 0, color: '#34574A', fontWeight: 700 }}>Ticket joviPlanet</h2>
        {unreadCount > 0 && (
          <span style={{
            background: '#ef4444',
            color: '#fff',
            borderRadius: '50%',
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            animation: 'pulse 2s infinite'
          }}>
            {unreadCount}
          </span>
        )}
      </div>
      {unreadCount > 0 && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          color: '#92400e',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <span style={{ fontSize: 18 }}>🔔</span>
          <span>Você tem {unreadCount} mensagem{unreadCount > 1 ? 's' : ''} não lida{unreadCount > 1 ? 's' : ''} {role === 'ADMIN' ? 'de clientes' : 'da equipe JoviPlanet'}.</span>
        </div>
      )}

      {showNewMessageAlert && (
        <div style={{
          background: '#dcfce7',
          border: '1px solid #22c55e',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          color: '#15803d',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          animation: 'fadeIn 0.3s ease-in-out'
        }}>
          <span style={{ fontSize: 18 }}>✨</span>
          <span>Você recebeu uma nova mensagem no ticket!</span>
        </div>
      )}

      <div style={{ width: '100%', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px #0001', padding: 24, marginBottom: 32, order: 2 }}>
        <h3 style={{ margin: '0 0 16px', color: '#34574A', fontWeight: 600 }}>Histórico de Tickets</h3>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setFilterArchived(false); setCurrentPage(1); }}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #34574A',
                background: !filterArchived ? '#34574A' : '#fff',
                color: !filterArchived ? '#fff' : '#34574A',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Ativos
            </button>
            <button
              onClick={() => { setFilterArchived(true); setCurrentPage(1); }}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: '1px solid #34574A',
                background: filterArchived ? '#34574A' : '#fff',
                color: filterArchived ? '#fff' : '#34574A',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Arquivados
            </button>
          </div>

          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, color: '#34574A' }}
          >
            <option value="">Todos os status</option>
            <option value="em_analise">Em análise</option>
            <option value="em_andamento">Em andamento</option>
            <option value="aguardando">Aguardando</option>
            <option value="resolvido">Resolvida</option>
            <option value="fechado">Fechado</option>
          </select>

          <select
            value={filterDays}
            onChange={(e) => { setFilterDays(Number(e.target.value)); setCurrentPage(1); }}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, color: '#34574A' }}
          >
            <option value={7}>Arquivar &gt; 7 dias</option>
            <option value={15}>Arquivar &gt; 15 dias</option>
            <option value={30}>Arquivar &gt; 30 dias</option>
            <option value={60}>Arquivar &gt; 60 dias</option>
            <option value={90}>Arquivar &gt; 90 dias</option>
            <option value={180}>Arquivar &gt; 180 dias</option>
          </select>

          <select
            value={filterDepartment}
            onChange={(e) => { setFilterDepartment(e.target.value); setCurrentPage(1); }}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, color: '#34574A' }}
          >
            <option value="">Todos os departamentos</option>
            {CONTACT_DEPARTMENTS.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>

          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar ID, assunto, código ou nome"
            style={{ flex: 1, minWidth: 220, padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, color: '#34574A' }}
          />

          {role === 'ADMIN' && (
            <input
              type="text"
              value={filterCustomer}
              onChange={(e) => { setFilterCustomer(e.target.value); setCurrentPage(1); }}
              placeholder="Código do cliente"
              style={{ width: 140, padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, color: '#34574A' }}
            />
          )}

          <button
            onClick={() => {
              setFilterStatus('');
              setFilterArchived(false);
              setFilterDays(30);
              setFilterDepartment('');
              setSearchTerm('');
              setSearch('');
              setFilterCustomer('');
              setCurrentPage(1);
            }}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: '1px solid #ccc',
              background: '#fff',
              color: '#34574A',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Limpar
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
            <thead>
              <tr style={{ background: '#f8f8f8' }}>
                <th style={{ padding: 10, textAlign: 'left', color: '#34574A' }}>ID</th>
                <th style={{ padding: 10, textAlign: 'left', color: '#34574A' }}>Data</th>
                <th style={{ padding: 10, textAlign: 'left', color: '#34574A' }}>Criado por</th>
                <th style={{ padding: 10, textAlign: 'left', color: '#34574A' }}>Origem</th>
                <th style={{ padding: 10, textAlign: 'left', color: '#34574A' }}>Assunto</th>
                <th style={{ padding: 10, textAlign: 'left', color: '#34574A' }}>Última interação</th>
                <th style={{ padding: 10, textAlign: 'left', color: '#34574A' }}>Status</th>
                <th style={{ padding: 10, textAlign: 'left', color: '#34574A' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {mediationsLoading && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 18 }}>Carregando...</td>
                </tr>
              )}

              {!mediationsLoading && mediationsError && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: '#d32f2f', padding: 18 }}>{mediationsError}</td>
                </tr>
              )}

              {!mediationsLoading && mediations.length === 0 && !mediationsError && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 18 }}>Nenhum Ticket registrado ainda.</td>
                </tr>
              )}

              {mediations.map((m) => {
                const hasUnread = getTicketUnreadCount(m, role) > 0;
                const origin = getTicketOrigin(m, fullName);
                const last = formatLastInteraction(m, role, nome);
                return (
                  <tr key={m.id} style={{ borderBottom: '1px solid #eee', background: hasUnread ? '#fffbeb' : 'transparent' }}>
                    <td style={{ padding: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{m.id}</span>
                        {hasUnread && (
                          <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, padding: '2px 6px', fontSize: 11, fontWeight: 700 }}>
                            {getTicketUnreadCount(m, role)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: 10 }}>
                      {m.data
                        ? (typeof m.data === 'string' ? m.data : new Date(m.data).toLocaleDateString('pt-BR'))
                        : new Date(m.date || m.createdAt).toLocaleDateString('pt-BR')
                      }
                    </td>
                    <td style={{ padding: 10 }}>{m.createdByName || '-'}</td>
                    <td style={{ padding: 10 }}>
                      <span style={{
                        display: 'inline-block',
                        background: origin === 'Enviado' ? '#34574A' : '#ba824f',
                        color: '#fff',
                        borderRadius: 12,
                        padding: '4px 10px',
                        fontSize: 12,
                        fontWeight: 500,
                      }}>
                        {origin}
                      </span>
                    </td>
                    <td style={{ padding: 10 }}>{m.subject || m.descricao || m.description}</td>
                    <td style={{ padding: 10 }}>
                      {last.text !== '—' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 11, color: '#61716b', fontWeight: 600 }}>{last.label}</span>
                            <span style={{ fontSize: 11, color: '#8a9691' }}>{last.time}</span>
                          </div>
                          <span style={{ fontSize: 13, color: '#333' }}>{last.text}</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: 13, color: '#888' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 10, textAlign: 'center', verticalAlign: 'middle' }}>{renderStatus(m)}</td>
                    <td style={{ padding: 10 }}>
                      <button
                        onClick={() => setSelectedMediation(m)}
                        style={{
                          background: hasUnread ? '#f59e0b' : '#34574A',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          fontSize: 14,
                        }}
                      >
                        {hasUnread ? `Ler (${getTicketUnreadCount(m, role)})` : 'Abrir'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16, padding: '0 24px' }}>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #ccc',
                background: currentPage === 1 ? '#f5f5f5' : '#fff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: 14,
              }}
            >
              Anterior
            </button>
            
            <span style={{ fontSize: 14, color: '#555' }}>
              Página {currentPage} de {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #ccc',
                background: currentPage === totalPages ? '#f5f5f5' : '#fff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: 14,
              }}
            >
              Próxima
            </button>
          </div>
        )}
      </div>

      <div style={{ width: '100%', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px #0001', padding: 24, order: 1 }}>
        <h3 style={{ marginBottom: 16, color: '#34574A', fontWeight: 600 }}>Abrir novo Ticket</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontWeight: 500, marginBottom: 4, display: 'block', color: '#34574A' }}>Nome</label>
              <input type="text" value={nome} disabled style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', background: '#f8f8f8' }} />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontWeight: 500, marginBottom: 4, display: 'block', color: '#34574A' }}>Código</label>
              <input type="text" value={String(Number(codigo) + 1000).padStart(4, '0')} disabled style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', background: '#f8f8f8' }} />
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={{ fontWeight: 500, marginBottom: 4, display: 'block', color: '#34574A' }}>Departamento</label>
              <select
                value={department}
                onChange={(e) => {
                  setDepartment(e.target.value);
                  if (submitSuccess) setSubmitSuccess(false);
                }}
                disabled={submitSuccess}
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', background: submitSuccess ? '#f8f8f8' : '#fff' }}
              >
                {CONTACT_DEPARTMENTS.map((d) => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </div>
            {role === 'ADMIN' && (
              <div style={{ flex: 1, minWidth: 120 }}>
                <label style={{ fontWeight: 500, marginBottom: 4, display: 'block', color: '#34574A' }}>ID do Cliente</label>
                <input
                  type="text"
                  value={customerUserId}
                  onChange={(e) => {
                    setCustomerUserId(e.target.value);
                    if (submitSuccess) setSubmitSuccess(false);
                  }}
                  placeholder="ID do cliente (opcional)"
                  disabled={submitSuccess}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', background: submitSuccess ? '#f8f8f8' : '#fff' }}
                />
              </div>
            )}
          </div>
          <div>
            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block', color: '#34574A' }}>Descreva sua solicitação</label>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (submitSuccess) setSubmitSuccess(false);
              }}
              required
              rows={4}
              disabled={submitSuccess}
              style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical', background: submitSuccess ? '#f8f8f8' : '#fff' }}
              placeholder="Explique o motivo da mediação..."
            />
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="submit"
              style={{
                background: '#ba824f',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '10px 24px',
                fontWeight: 600,
                cursor: isSubmitting || submitSuccess ? 'not-allowed' : 'pointer',
                fontSize: 16,
                opacity: isSubmitting || submitSuccess ? 0.6 : 1,
              }}
              disabled={!message.trim() || isSubmitting || submitSuccess}
            >
              {isSubmitting ? 'Enviando...' : submitSuccess ? 'Enviado' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>

      {selectedMediation && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 16,
              minWidth: 500, maxWidth: 780, width: '95vw',
              maxHeight: '92vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 8px 40px #0003', overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{ background: '#34574A', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>
                  Ticket #{selectedMediation.id}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 2 }}>
                  {selectedMediation.createdByName || 'N/D'} • {selectedMediation.data || new Date(selectedMediation.createdAt).toLocaleDateString('pt-BR')}
                  {selectedMediation.departamento && ` • ${getDepartmentLabel(selectedMediation.departamento)}`}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginTop: 6 }}>
                  {selectedMediation.subject || selectedMediation.description || selectedMediation.descricao || 'Acompanhe aqui todo o histórico do atendimento.'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {renderStatus(selectedMediation)}
                <button onClick={() => setSelectedMediation(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 20, width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  &times;
                </button>
              </div>
            </div>

            {/* Admin controls */}
            {role === 'ADMIN' && (
              <div style={{ background: '#f8f8f8', padding: '10px 24px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, flexWrap: 'wrap' }}>
                <label style={{ fontWeight: 600, color: '#34574A', fontSize: 13 }}>Status:</label>
                <select
                  value={adminStatus}
                  onChange={(e) => setAdminStatus(e.target.value)}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                >
                  <option value="aberto">Em análise</option>
                  <option value="em_andamento">Em andamento</option>
                  <option value="resolvido">Resolvida</option>
                  <option value="fechado">Fechado</option>
                </select>
                <button
                  onClick={handleUpdateStatus}
                  disabled={savingPatch}
                  style={{ background: '#34574A', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
                >
                  {savingPatch ? 'Salvando...' : 'Salvar status'}
                </button>
              </div>
            )}

            {/* Chat area */}
            <div style={{ padding: '12px 24px', background: '#fff', borderBottom: '1px solid #e8ecea', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', color: '#61716b', fontSize: 12 }}>
                <strong style={{ color: '#34574A', fontSize: 13 }}>Histórico da conversa</strong>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#34574A' }} /> Você</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ba824f' }} /> ADMIN / Suporte</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#d9dee0' }} /> Cliente</span>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, background: '#f5f6f8' }}>
              {(selectedMediation.messages || []).length === 0 && (
                <div style={{ color: '#aaa', fontSize: 14, textAlign: 'center', marginTop: 40 }}>Nenhuma mensagem ainda.</div>
              )}
              {(selectedMediation.messages || []).map((msg, idx) => {
                const presentation = getMessagePresentation(msg, role, nome);
                const msgAttachments = msg.attachments || [];
                const isUser = presentation.isOwnMessage;
                return (
                  <div key={msg.id || idx} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', flexDirection: isUser ? 'row-reverse' : 'row' }}>
                    <div title={presentation.senderName} style={{ flex: '0 0 32px', width: 32, height: 32, borderRadius: '50%', background: isUser ? '#34574A' : (presentation.isSupportMessage ? '#ba824f' : '#d9dee0'), color: isUser || presentation.isSupportMessage ? '#fff' : '#52605b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                      {presentation.initials}
                    </div>
                    <div style={{ maxWidth: '78%', minWidth: 100, display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, flexWrap: 'wrap', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                        <strong style={{ color: '#34574A', fontSize: 12 }}>{presentation.senderName}</strong>
                        <span style={{ color: '#8a9691', fontSize: 11, fontWeight: 600 }}>{presentation.senderLabel}</span>
                        <span style={{ color: '#9aa49f', fontSize: 11 }}>{msg.createdAt ? new Date(msg.createdAt).toLocaleString('pt-BR') : '—'}</span>
                      </div>
                    <div style={{
                      width: '100%', padding: '11px 14px', borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: isUser ? '#34574A' : (presentation.isSupportMessage ? '#fff5eb' : '#fff'),
                      color: isUser ? '#fff' : '#333',
                      border: isUser ? 'none' : '1px solid #e7ebe9',
                      boxShadow: '0 1px 4px #0001',
                      fontSize: 14, lineHeight: 1.5,
                    }}>
                      {msg.content?.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '')}
                      {msgAttachments.length > 0 && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${isUser ? 'rgba(255,255,255,0.3)' : '#eee'}` }}>
                          {msgAttachments.map((file, aidx) => (
                            <div key={aidx} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span>📎</span>
                              <span>{getAttachmentName(file, aidx)}</span>
                              {buildAttachmentUrl(file) && (
                                <button type="button" onClick={() => viewAttachment(file, aidx)} style={{ background: 'none', border: 'none', color: isUser ? '#c8e6c9' : '#34574A', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 12 }}>
                                  abrir
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>

            {/* Preview */}
            {previewUrl && (
              <div style={{ padding: '10px 24px', background: '#fafafa', borderTop: '1px solid #eee', flexShrink: 0, textAlign: 'center', position: 'relative' }}>
                <button
                  onClick={() => { setPreviewUrl(''); setPreviewName(''); setPreviewType(''); }}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 24,
                    background: '#d32f2f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    cursor: 'pointer',
                    fontSize: 18,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  &times;
                </button>
                <div style={{ fontWeight: 600, color: '#34574A', fontSize: 13, marginBottom: 6 }}>Pré-visualização: {previewName}</div>
                {previewType?.startsWith('image/') || previewType === 'image/*' ? (
                  <img src={previewUrl} alt={previewName} style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 6 }} />
                ) : (
                  <iframe title={previewName} src={previewUrl} style={{ width: '100%', height: 200, border: '1px solid #ddd', borderRadius: 6 }} />
                )}
              </div>
            )}

            {/* Reply bar */}
            {selectedMediation.status !== 'fechado' && (
              <div style={{ padding: '12px 24px', background: '#fff', borderTop: '1px solid #eee', flexShrink: 0 }}>
                <form onSubmit={handleReply} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={2}
                    placeholder="Escreva sua resposta..."
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd', resize: 'none', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (replyMessage.trim()) handleReply(e); } }}
                  />
                  <button
                    type="submit"
                    disabled={sendingReply || !replyMessage.trim()}
                    style={{ background: '#34574A', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14, flexShrink: 0 }}
                  >
                    {sendingReply ? '...' : 'Enviar'}
                  </button>
                </form>
                {replySuccess && (
                  <div style={{ color: '#22c55e', background: '#e7fbe7', borderRadius: 8, padding: '8px 12px', marginTop: 8, fontSize: 13, fontWeight: 500, textAlign: 'center' }}>
                    ✓ Resposta enviada com sucesso!
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  <form onSubmit={handleUpload} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="file" onChange={(e) => setFileToAttach(e.target.files[0])} style={{ fontSize: 13, flex: 1 }} />
                    <button type="submit" disabled={uploading || !fileToAttach} style={{ background: '#ba824f', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                      {uploading ? 'Anexando...' : '📎 Anexar'}
                    </button>
                  </form>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Modal de Erro */}
      {showErrorModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '32px 40px',
              maxWidth: 400,
              width: '90%',
              boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: '#ffebee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <span style={{ fontSize: 32, color: '#d32f2f' }}>⚠️</span>
            </div>
            <h3 style={{ margin: '0 0 12px', color: '#34574A', fontSize: 20, fontWeight: 700 }}>
              Erro
            </h3>
            <p style={{ margin: '0 0 24px', color: '#555', fontSize: 15, lineHeight: 1.5 }}>
              {errorMessage}
            </p>
            <button
              onClick={() => setShowErrorModal(false)}
              style={{
                background: '#34574A',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 32px',
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => e.target.style.background = '#2a4a3f'}
              onMouseOut={(e) => e.target.style.background = '#34574A'}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '32px 40px',
              maxWidth: 450,
              width: '90%',
              boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#e7fbe7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <span style={{ fontSize: 40, color: '#22c55e' }}>✓</span>
            </div>
            <h3 style={{ margin: '0 0 16px', color: '#34574A', fontSize: 22, fontWeight: 700 }}>
              Ticket Enviado com Sucesso!
            </h3>
            <p style={{ margin: '0 0 28px', color: '#555', fontSize: 15, lineHeight: 1.6 }}>
              A Sua mensagem foi enviada com sucesso. Fique atento nas próximas horas. A equipa JoviPlanet já está em orbita.
            </p>
            <button
              onClick={handleResetForm}
              style={{
                background: '#34574A',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 32px',
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => e.target.style.background = '#2a4a3f'}
              onMouseOut={(e) => e.target.style.background = '#34574A'}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMediation;
